import { useEffect, useState } from 'react'

import { AddEditAdditionalDepreciation } from './components/modals/AddEditAdditionalDepreciation'
import { AdditionalExpensesFilters } from '@pages/OtherExpenses/components/AdditionalExpensesFilters'
import { budgetPreviews } from './components/BudgetPreviews'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { currencyMask } from '@utils/masks/currencyMask'
import { EditDetailedSummaryDepreciation } from './components/modals/EditDetailedSummaryDepreciation'
import { emotionTheme } from '@styles/theme'
import { getPagePermissions } from '@hooks/useUser/provider'
import { initialPagination, type PaginationParams } from '@components/shared/Table/components/Pagination/types'
import { PageIds } from '@hooks/useLayout/types'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseAdditionalDepreciationGrid } from './helpers/parseAdditionalDepreciationGrid'
import { parseBudgetPreviewsGrid } from './helpers/parseBudgetPreviewsGrid'
import { parseDetailedSummaryGrid } from './helpers/parseDetailedSummaryGrid'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnIcon } from '@assets/svgs/ReturnIcon'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { TrashSimple } from '@assets/svgs/TrashSimple'
import { useDepreciation } from './context'
import { useMediaQuery } from '@mui/material'
import { useUser } from '@hooks/useUser'
import { PREVIEW_ACCOUNTS, PreviewHistoryDrawer } from '@components/shared/PreviewHistoryDrawer'
// import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'
import { WarningBanner } from '@components/shared/WarningBanner'
import { WarningIcon } from '@phosphor-icons/react'

import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'
import type { AdditionalDepreciationItemProps, DetailedSummaryDepreciationItemProps } from './context/types'
import type { AdditionalExpensesFiltersFormProps } from '@pages/OtherExpenses/components/AdditionalExpensesFilters/form/types'
import type { SortProps } from '@components/shared/Table/types'

// RC07: tabela de depreciações adicionais abre com 20 itens por página
const ADDITIONAL_DEPRECIATION_ITEMS_PER_PAGE = 20

import * as S from './styles'

export const Depreciation = () => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData, userPermissionsData } = useUser()
  // const isGUProfile = userPermissionsData?.profile?.name === PROFILE_GU
  const {
    detailedSummaryDepreciationData,
    isDetailedSummaryDepreciationLoading,
    getDetailedSummaryDepreciation,
    isBigNumbersDepreciationLoading,
    getBigNumbersDepreciation,
    bigNumbersDepreciationData,
    isApprovalWorkflowDepreciationLoading,
    approvalWorkflowDepreciation,
    isReversePreviewDepreciationLoading,
    reversePreviewDepreciation,
    isReleasePreviewDepreciationLoading,
    releasePreviewDepreciation,
    additionalDepreciationData,
    isAdditionalDepreciationLoading,
    getAdditionalDepreciation,
    getAdditionalDepreciationAccounts,
    removeAdditionalDepreciation,
    isRemoveAdditionalDepreciationLoading
  } = useDepreciation()

  const [additionalDepreciationFilters, setAdditionalDepreciationFilters] =
    useState<AdditionalExpensesFiltersFormProps | null>(null)
  const [additionalDepreciationItem, setAdditionalDepreciationItem] = useState<AdditionalDepreciationItemProps | null>(
    null
  )
  const [openAddEditAdditionalDepreciationModal, setOpenAddEditAdditionalDepreciationModal] = useState(false)
  const [openDeleteAdditionalDepreciationModal, setOpenDeleteAdditionalDepreciationModal] = useState(false)

  const [openEditDetailedSummaryDepreciationModal, setOpenEditDetailedSummaryDepreciationModal] = useState(false)
  const [detailedSummaryDepreciationFormData, setDetailedSummaryDepreciationFormData] =
    useState<DetailedSummaryDepreciationItemProps | null>(null)
  const [openApprovalWorkflowModal, setOpenApprovalWorkflowModal] = useState(false)
  const [openReleasePreviewModal, setOpenReleasePreviewModal] = useState(false)
  const [openReversePreviewModal, setOpenReversePreviewModal] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const [historyWeekNumber, setHistoryWeekNumber] = useState<number | null>(null)
  const canEditFromPreview = bigNumbersDepreciationData?.canEdit === true
  const canEditPage = canEditFromPreview && permissions?.canEdit === true
  // a listagem também devolve canEdit/corporateFoodSegmentMessage: qualquer um bloqueando trava a tabela inteira
  const canEditAdditionalDepreciation =
    canEditPage &&
    additionalDepreciationData?.canEdit !== false &&
    !additionalDepreciationData?.corporateFoodSegmentMessage

  const handleOpenAddEditAdditionalDepreciation = (data: AdditionalDepreciationItemProps | null) => {
    setAdditionalDepreciationItem(data)
    setOpenAddEditAdditionalDepreciationModal(true)
  }

  const handleOpenDeleteAdditionalDepreciation = (data: AdditionalDepreciationItemProps) => {
    setAdditionalDepreciationItem(data)
    setOpenDeleteAdditionalDepreciationModal(true)
  }

  const handleGetAdditionalDepreciation = (
    params: PaginationParams | null,
    filters?: AdditionalExpensesFiltersFormProps | null,
    sortData?: SortProps
  ) => {
    getAdditionalDepreciation({
      ...sortData,
      ...(filters || additionalDepreciationFilters),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage:
        params?.itemsPerPage || additionalDepreciationData?.itemsPerPage || ADDITIONAL_DEPRECIATION_ITEMS_PER_PAGE
    })
    !!filters && setAdditionalDepreciationFilters(filters)
  }

  const handleOpenEditDetailedSummaryDepreciation = (data: DetailedSummaryDepreciationItemProps) => {
    setOpenEditDetailedSummaryDepreciationModal(true)
    setDetailedSummaryDepreciationFormData(data)
  }

  const handleGetDetailedSummaryDepreciation = (
    params: PaginationParams | null,
    formData?: DetailedSummaryDepreciationItemProps | null,
    sortData?: SortProps
  ) => {
    getDetailedSummaryDepreciation({
      ...sortData,
      ...(formData || detailedSummaryDepreciationFormData),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage:
        params?.itemsPerPage || detailedSummaryDepreciationData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setDetailedSummaryDepreciationFormData(formData)
  }

  const handleInitialActionsFromScreen = () => {
    if (filiData?.filiCode && filiData?.filiCode !== 'ALL') {
      const payload = { filiCode: filiData?.filiCode!, competenceDate: filiData?.startPeriod! }

      handleGetDetailedSummaryDepreciation(null)
      handleGetAdditionalDepreciation(null)
      getBigNumbersDepreciation(payload)
    }
  }

  useEffect(() => {
    getAdditionalDepreciationAccounts()
  }, [])

  useEffect(() => {
    handleInitialActionsFromScreen()
  }, [filiData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.DEPRECIATION)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return (
    <>
      <PageWrapper
        breadcrumbData={[
          { label: 'Gestão Financeira' },
          { label: `Depreciação / ${filiData?.filiCode} - ${filiData?.filiName}` }
        ]}
      >
        <WarningBanner message={bigNumbersDepreciationData?.corporateFoodSegmentMessage} />

        <Title label="Depreciação" />

        <BudgetPreviewsWrapper>
          {isMobile ? (
            <Slider items={budgetPreviews({ data: bigNumbersDepreciationData })} />
          ) : (
            <PreviewWrapper>{budgetPreviews({ data: bigNumbersDepreciationData })}</PreviewWrapper>
          )}

          <Table
            data={parseBudgetPreviewsGrid({ data: bigNumbersDepreciationData?.previews || [], onViewHistory: setHistoryWeekNumber })}
            isLoading={isBigNumbersDepreciationLoading}
          />
        </BudgetPreviewsWrapper>

        <Title label="Resumo detalhado" marginBottom={8} />

        <AdditionalExpensesFilters
          isLoading={isDetailedSummaryDepreciationLoading}
          data={detailedSummaryDepreciationData?.availableFilters || null}
          onFilter={(formData) =>
            handleGetDetailedSummaryDepreciation(null, formData as DetailedSummaryDepreciationItemProps)
          }
        />

        <Table
          data={parseDetailedSummaryGrid({
            data: detailedSummaryDepreciationData?.items || [],
            onEdit: handleOpenEditDetailedSummaryDepreciation,
            actionsDisabled: !canEditPage
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  {
                    label: 'Total Depreciação',
                    value: currencyMask(detailedSummaryDepreciationData?.totalValue?.toFixed(2)) || 0
                  }
                ]}
              />
            )
          }}
          footer={{
            pagination: {
              currentPage: detailedSummaryDepreciationData?.currentPage,
              itemsPerPage: detailedSummaryDepreciationData?.itemsPerPage,
              totalItems: detailedSummaryDepreciationData?.totalItems,
              totalPages: detailedSummaryDepreciationData?.totalPages,
              onPageChange: (page) => handleGetDetailedSummaryDepreciation({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetDetailedSummaryDepreciation({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetDetailedSummaryDepreciation(null, null, sortData)}
          isLoading={isDetailedSummaryDepreciationLoading}
        />

        <Title label="Depreciações Adicionais" marginBottom={8} />

        <AdditionalExpensesFilters
          isLoading={isAdditionalDepreciationLoading}
          data={additionalDepreciationData?.availableFilters || null}
          onFilter={(filters) => handleGetAdditionalDepreciation(null, filters)}
        />

        <Table
          data={parseAdditionalDepreciationGrid({
            data: additionalDepreciationData?.items || [],
            onEdit: handleOpenAddEditAdditionalDepreciation,
            onDelete: handleOpenDeleteAdditionalDepreciation,
            actionsDisabled: !canEditAdditionalDepreciation
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  {
                    label: 'Total Depreciações Adicionais',
                    value: currencyMask(additionalDepreciationData?.totalValue?.toFixed(2)) || 0
                  }
                ]}
              />
            )
          }}
          footer={{
            actionButton: {
              labelButton: 'Adicionar Depreciação',
              onClick: () => handleOpenAddEditAdditionalDepreciation(null),
              disabled: !canEditAdditionalDepreciation
            },
            pagination: {
              currentPage: additionalDepreciationData?.currentPage,
              itemsPerPage: additionalDepreciationData?.itemsPerPage,
              totalItems: additionalDepreciationData?.totalItems,
              totalPages: additionalDepreciationData?.totalPages,
              onPageChange: (page) => handleGetAdditionalDepreciation({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetAdditionalDepreciation({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetAdditionalDepreciation(null, null, sortData)}
          isLoading={isAdditionalDepreciationLoading}
        />

        <S.Buttons>
          {bigNumbersDepreciationData?.allowRevertPreview && (
            <Button
              width={isMobile ? '100%' : 190}
              colorType="outlineBlue"
              onClick={() => setOpenReversePreviewModal(true)}
            >
              Reverter Prévia
            </Button>
          )}
        </S.Buttons>

        {canEditPage && (
          <S.Buttons>
            {/* <Tooltip
              width="fit-content"
              title="Esse botão foi adicionado para facilitar os testes, após a validação da tela ele será removido"
              placement="top"
            >
              <Button
                width={isMobile ? '100%' : 190}
                colorType="outlineBlue"
                onClick={() =>
                  reverseWorkflowDepreciation({
                    filiCode: filiData?.filiCode!,
                    competenceDate: filiData?.startPeriod!
                  })
                }
                disabled={
                  isReverseWorkflowDepreciationLoading ||
                  isBigNumbersDepreciationLoading ||
                  !(hasInProgressPreview || hasCompletedPreview)
                }
                loading={isReverseWorkflowDepreciationLoading}
              >
                Restaurar Prévia
              </Button>
            </Tooltip> */}

            {bigNumbersDepreciationData?.allowReleasePreview && (
              <Button
                width={isMobile ? '100%' : 190}
                colorType="solidBlue"
                onClick={() => setOpenReleasePreviewModal(true)}
              >
                Liberar Prévia
              </Button>
            )}
          </S.Buttons>
        )}
      </PageWrapper>

      <EditDetailedSummaryDepreciation
        data={detailedSummaryDepreciationFormData}
        open={openEditDetailedSummaryDepreciationModal}
        onClose={setOpenEditDetailedSummaryDepreciationModal}
        canEdit={canEditPage}
      />

      <AddEditAdditionalDepreciation
        data={additionalDepreciationItem}
        open={openAddEditAdditionalDepreciationModal}
        onClose={() => setOpenAddEditAdditionalDepreciationModal(false)}
        canEdit={canEditAdditionalDepreciation}
      />

      <ReturnModal
        open={openDeleteAdditionalDepreciationModal}
        headerIcon={<TrashSimple />}
        headerTitle="Excluir Depreciação?"
        title="Você tem certeza de que deseja excluir esta depreciação?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenDeleteAdditionalDepreciationModal}
        isLoading={isRemoveAdditionalDepreciationLoading}
        onConfirm={() => {
          if (!canEditAdditionalDepreciation || !additionalDepreciationItem) return

          removeAdditionalDepreciation({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!,
            id: additionalDepreciationItem.id
          }).then((isSuccess) => {
            if (isSuccess) {
              setOpenDeleteAdditionalDepreciationModal(false)
              setAdditionalDepreciationItem(null)
            }
          })
        }}
      />

      <ReturnModal
        open={openApprovalWorkflowModal}
        headerIcon={<ReturnIcon />}
        headerTitle={`Prévia Depreciação - ${bigNumbersDepreciationData?.currentPreviewNumber}ª semana`}
        title="Você tem certeza de que deseja enviar a prévia?"
        message="A prévia será enviada para aprovação."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenApprovalWorkflowModal}
        isLoading={isApprovalWorkflowDepreciationLoading}
        onConfirm={() => {
          if (!canEditPage) return

          approvalWorkflowDepreciation({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenApprovalWorkflowModal(false))
        }}
      />

      <ReturnModal
        open={openReleasePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Liberar Prévia"
        message={`Tem certeza de que deseja liberar a ${bigNumbersDepreciationData?.currentPreviewNumber}ª Prévia de Depreciação?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReleasePreviewModal}
        isLoading={isReleasePreviewDepreciationLoading}
        onConfirm={() => {
          if (!canEditPage) return

          releasePreviewDepreciation({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReleasePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openReversePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Reverter Prévia"
        message={`Tem certeza de que deseja reverter a ${bigNumbersDepreciationData?.currentPreviewNumber}ª Prévia de Depreciação?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReversePreviewModal}
        isLoading={isReversePreviewDepreciationLoading}
        onConfirm={() => {
          reversePreviewDepreciation({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReversePreviewModal(false))
        }}
      />

      <PreviewHistoryDrawer
        pageLabel="Depreciação"
        accounts={[PREVIEW_ACCOUNTS.depreciation]}
        weekNumber={historyWeekNumber}
        onClose={() => setHistoryWeekNumber(null)}
      />
    </>
  )
}
